import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ArrowLeft, Save, Plus, Trash2, GripVertical, Edit3, Check, X, Copy, ChevronUp, ChevronDown } from "lucide-react";
import CourseThumbnailUploader from "@/components/admin/CourseThumbnailUploader";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const CATEGORIES = [
  "Marketing", "AI & Automation", "Creative", "Social Media", "Paid Ads",
  "Development", "Business", "Career", "Personal Growth", "Other",
];
const DIFFICULTIES = ["Beginner", "Intermediate", "Advanced", "Beginner to Advanced"];

/* ---------- Sortable lesson row ---------- */
function SortableLesson({ lesson, courseId, moduleId, onDeleted, onEdited }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: lesson.id });
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    title: lesson.title, video_id: lesson.video_id, duration_min: lesson.duration_min, description: lesson.description || "",
  });

  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };

  async function save() {
    try {
      await api.put(`/admin/courses/${courseId}/lessons/${lesson.id}`, form);
      toast.success("Lesson updated");
      setEditing(false); onEdited();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  }
  async function remove() {
    try {
      await api.delete(`/admin/courses/${courseId}/lessons/${lesson.id}`);
      toast.success("Lesson deleted"); onDeleted();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  }

  return (
    <div ref={setNodeRef} style={style}
         data-testid={`lesson-row-${lesson.id}`}
         className="rounded-2xl border border-border bg-background p-3 flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <button {...attributes} {...listeners} data-testid={`drag-handle-${lesson.id}`}
                className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground">
          <GripVertical className="h-4 w-4" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm truncate">{lesson.title}</p>
          <p className="text-xs text-muted-foreground">
            <span className="font-mono">{lesson.video_id || "no-video"}</span> · {lesson.duration_min} min
          </p>
        </div>
        <Button size="sm" variant="ghost" onClick={() => setEditing(!editing)} data-testid={`edit-lesson-${lesson.id}`}>
          <Edit3 className="h-3.5 w-3.5" />
        </Button>
        <Button size="sm" variant="ghost" onClick={async () => {
          try { await api.post(`/admin/courses/${courseId}/lessons/${lesson.id}/duplicate`); toast.success("Duplicated"); onEdited(); }
          catch (e) { toast.error(e.message); }
        }} data-testid={`duplicate-lesson-${lesson.id}`} title="Duplicate">
          <Copy className="h-3.5 w-3.5" />
        </Button>
        <Button size="sm" variant="ghost" onClick={async () => {
          try { await api.patch(`/admin/courses/${courseId}/lessons/${lesson.id}/move`, { direction: "up" }); onEdited(); }
          catch (e) { toast.error(e.message); }
        }} data-testid={`up-lesson-${lesson.id}`} title="Move up">
          <ChevronUp className="h-3.5 w-3.5" />
        </Button>
        <Button size="sm" variant="ghost" onClick={async () => {
          try { await api.patch(`/admin/courses/${courseId}/lessons/${lesson.id}/move`, { direction: "down" }); onEdited(); }
          catch (e) { toast.error(e.message); }
        }} data-testid={`down-lesson-${lesson.id}`} title="Move down">
          <ChevronDown className="h-3.5 w-3.5" />
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button size="sm" variant="ghost" className="text-destructive" data-testid={`delete-lesson-${lesson.id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete lesson?</AlertDialogTitle>
              <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={remove} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      {editing && (
        <div className="grid sm:grid-cols-2 gap-3 pl-7">
          <div className="sm:col-span-2">
            <Label className="text-xs">Title</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 rounded-lg h-9" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">YouTube URL or ID</Label>
            <Input value={form.video_id} onChange={(e) => setForm({ ...form, video_id: e.target.value })}
              placeholder="https://youtu.be/... or 11-char ID"
              className="mt-1 rounded-lg h-9 font-mono text-xs" />
          </div>
          <div>
            <Label className="text-xs">Duration (min)</Label>
            <Input type="number" value={form.duration_min}
              onChange={(e) => setForm({ ...form, duration_min: parseInt(e.target.value) || 0 })}
              className="mt-1 rounded-lg h-9" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Description</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="mt-1 rounded-lg min-h-[70px]" />
          </div>
          <div className="sm:col-span-2 flex gap-2 justify-end">
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}><X className="h-3.5 w-3.5 mr-1" />Cancel</Button>
            <Button size="sm" onClick={save} data-testid={`save-lesson-${lesson.id}`}
              className="royal-gradient border-0 text-white"><Check className="h-3.5 w-3.5 mr-1" />Save</Button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Module block ---------- */
function ModuleBlock({ courseId, module, onChange }) {
  const [lessons, setLessons] = useState(module.lessons || []);
  const [addingLesson, setAddingLesson] = useState(false);
  const [newLesson, setNewLesson] = useState({ title: "", video_id: "", duration_min: 10 });
  const [renaming, setRenaming] = useState(false);
  const [title, setTitle] = useState(module.title);

  useEffect(() => { setLessons(module.lessons || []); setTitle(module.title); }, [module]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  async function handleDragEnd(evt) {
    const { active, over } = evt;
    if (!over || active.id === over.id) return;
    const oldIdx = lessons.findIndex((l) => l.id === active.id);
    const newIdx = lessons.findIndex((l) => l.id === over.id);
    const next = arrayMove(lessons, oldIdx, newIdx);
    setLessons(next);
    try {
      await api.patch(`/admin/courses/${courseId}/reorder-lessons`, {
        order: { [module.id]: next.map((l) => l.id) },
      });
      toast.success("Lessons reordered");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      onChange(); // reload from server
    }
  }

  async function addLesson() {
    if (!newLesson.title || !newLesson.video_id) return toast.error("Title and YouTube URL/ID required");
    try {
      await api.post(`/admin/courses/${courseId}/lessons`, {
        module_id: module.id, ...newLesson,
      });
      toast.success("Lesson added");
      setNewLesson({ title: "", video_id: "", duration_min: 10 }); setAddingLesson(false); onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  }

  async function renameModule() {
    if (!title.trim()) return;
    try {
      await api.put(`/admin/courses/${courseId}/modules/${module.id}`, { title });
      toast.success("Module renamed"); setRenaming(false); onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  }

  async function deleteModule() {
    try {
      await api.delete(`/admin/courses/${courseId}/modules/${module.id}`);
      toast.success("Module deleted"); onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  }

  return (
    <div className="rounded-3xl bg-card border border-border overflow-hidden" data-testid={`module-block-${module.id}`}>
      <div className="px-5 py-4 border-b border-border bg-secondary/40 flex items-center gap-3 flex-wrap">
        {renaming ? (
          <div className="flex items-center gap-2 flex-1">
            <Input value={title} onChange={(e) => setTitle(e.target.value)}
              data-testid={`module-title-input-${module.id}`}
              className="rounded-lg h-9 max-w-xs" />
            <Button size="sm" onClick={renameModule} data-testid={`save-module-${module.id}`}
              className="royal-gradient border-0 text-white"><Check className="h-3.5 w-3.5" /></Button>
            <Button size="sm" variant="ghost" onClick={() => { setRenaming(false); setTitle(module.title); }}><X className="h-3.5 w-3.5" /></Button>
          </div>
        ) : (
          <>
            <p className="font-display font-bold flex-1">{module.title}</p>
            <span className="text-xs text-muted-foreground">{lessons.length} lessons</span>
            <Button size="sm" variant="ghost" onClick={() => setRenaming(true)} data-testid={`rename-module-${module.id}`}>
              <Edit3 className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" variant="ghost" onClick={async () => {
              try { await api.post(`/admin/courses/${courseId}/modules/${module.id}/duplicate`); toast.success("Module duplicated"); onChange(); }
              catch (e) { toast.error(e.message); }
            }} data-testid={`duplicate-module-${module.id}`} title="Duplicate">
              <Copy className="h-3.5 w-3.5" />
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="ghost" className="text-destructive" data-testid={`delete-module-${module.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete module "{module.title}"?</AlertDialogTitle>
                  <AlertDialogDescription>All lessons in this module will be removed. This cannot be undone.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={deleteModule} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        )}
      </div>

      <div className="p-4 space-y-2">
        {lessons.length === 0 && <p className="text-sm text-muted-foreground py-3 text-center">No lessons yet. Add the first one below.</p>}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={lessons.map((l) => l.id)} strategy={verticalListSortingStrategy}>
            {lessons.map((l) => (
              <SortableLesson key={l.id} lesson={l} courseId={courseId} moduleId={module.id}
                onDeleted={onChange} onEdited={onChange} />
            ))}
          </SortableContext>
        </DndContext>

        {addingLesson ? (
          <div className="rounded-2xl border border-primary/30 bg-primary/5 p-3 space-y-2">
            <Input value={newLesson.title} onChange={(e) => setNewLesson({ ...newLesson, title: e.target.value })}
              placeholder="Lesson title" data-testid={`new-lesson-title-${module.id}`}
              className="rounded-lg h-10" />
            <Input value={newLesson.video_id} onChange={(e) => setNewLesson({ ...newLesson, video_id: e.target.value })}
              placeholder="YouTube unlisted URL or 11-char ID"
              data-testid={`new-lesson-video-${module.id}`}
              className="rounded-lg h-10 font-mono text-xs" />
            <div className="flex items-center gap-2">
              <Input type="number" value={newLesson.duration_min}
                onChange={(e) => setNewLesson({ ...newLesson, duration_min: parseInt(e.target.value) || 0 })}
                placeholder="Duration (min)" className="rounded-lg h-10 max-w-[130px]" />
              <Button onClick={addLesson} data-testid={`save-new-lesson-${module.id}`}
                className="rounded-full royal-gradient border-0 text-white">
                <Plus className="h-4 w-4 mr-1" /> Add
              </Button>
              <Button variant="ghost" onClick={() => setAddingLesson(false)}>Cancel</Button>
            </div>
          </div>
        ) : (
          <Button variant="outline" onClick={() => setAddingLesson(true)}
            data-testid={`add-lesson-btn-${module.id}`}
            className="w-full rounded-xl border-dashed h-11">
            <Plus className="h-4 w-4 mr-1.5" /> Add lesson
          </Button>
        )}
      </div>
    </div>
  );
}

/* ---------- Main editor ---------- */
export default function AdminCourseEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [course, setCourse] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newModuleTitle, setNewModuleTitle] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/courses/${id}`);
      setCourse(r.data);
    } catch (e) { toast.error("Course not found"); nav("/app/admin/courses"); }
  }, [id, nav]);

  useEffect(() => { load(); }, [load]);

  if (!course) return <div className="h-64 flex items-center justify-center"><div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;

  function upd(k, v) { setCourse((c) => ({ ...c, [k]: v })); }

  async function saveDetails() {
    if (!course.title || !course.slug) return toast.error("Title and slug are required");
    setSaving(true);
    try {
      await api.put(`/admin/courses/${course.id}`, {
        title: course.title, slug: course.slug, tagline: course.tagline || "",
        description: course.description || "", trainer: course.trainer || "",
        duration_hours: course.duration_hours || 0, difficulty: course.difficulty || "Beginner",
        thumbnail: course.thumbnail || "", category: course.category || "Marketing",
        featured: !!course.featured, published: course.published ?? true,
      });
      toast.success("Course details saved");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  }

  async function togglePublish() {
    const next = !(course.published ?? true);
    try {
      await api.patch(`/admin/courses/${course.id}/publish`, { published: next });
      toast.success(next ? "Course published" : "Course unpublished");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  }

  async function addModule() {
    if (!newModuleTitle.trim()) return toast.error("Module title required");
    try {
      await api.post(`/admin/courses/${course.id}/modules`, { title: newModuleTitle });
      toast.success("Module added"); setNewModuleTitle(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  }

  const published = course.published ?? true;

  return (
    <div className="space-y-6" data-testid="admin-course-editor">
      <div className="flex items-center gap-3 flex-wrap">
        <Link to="/app/admin/courses" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary">
          <ArrowLeft className="h-4 w-4" /> All courses
        </Link>
        <span className="text-muted-foreground text-sm">·</span>
        <h2 className="font-display text-2xl font-bold truncate">{course.title}</h2>
        <span className={`ml-auto rounded-full px-3 py-1 text-xs font-semibold ${published ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" : "bg-secondary text-muted-foreground"}`}>
          {published ? "Published" : "Draft"}
        </span>
        <Button variant="outline" onClick={togglePublish} data-testid="editor-toggle-publish" className="rounded-full">
          {published ? "Unpublish" : "Publish"}
        </Button>
      </div>

      <Tabs defaultValue="details">
        <TabsList className="rounded-full bg-secondary/60">
          <TabsTrigger value="details" className="rounded-full">Details</TabsTrigger>
          <TabsTrigger value="curriculum" className="rounded-full">Curriculum</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="mt-5">
          <div className="rounded-3xl bg-card border border-border p-6 space-y-5 max-w-3xl">
            <CourseThumbnailUploader
              value={course.thumbnail}
              onChange={(u) => upd("thumbnail", u)}
              onImmediateSave={async (url) => {
                await api.put(`/admin/courses/${course.id}`, {
                  title: course.title, slug: course.slug, tagline: course.tagline || "",
                  description: course.description || "", trainer: course.trainer || "",
                  duration_hours: course.duration_hours || 0, difficulty: course.difficulty || "Beginner",
                  thumbnail: url, category: course.category || "Marketing",
                  featured: !!course.featured, published: course.published ?? true,
                });
                load();
              }}
            />

            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Title</Label>
                <Input value={course.title} onChange={(e) => upd("title", e.target.value)}
                  data-testid="editor-title" className="mt-1.5 rounded-xl h-11" />
              </div>
              <div><Label>Slug</Label>
                <Input value={course.slug} onChange={(e) => upd("slug", e.target.value)}
                  data-testid="editor-slug" className="mt-1.5 rounded-xl h-11 font-mono text-sm" />
              </div>
            </div>
            <div><Label>Tagline</Label>
              <Input value={course.tagline || ""} onChange={(e) => upd("tagline", e.target.value)} className="mt-1.5 rounded-xl h-11" />
            </div>
            <div><Label>Description</Label>
              <Textarea value={course.description || ""} onChange={(e) => upd("description", e.target.value)}
                data-testid="editor-description" className="mt-1.5 rounded-2xl min-h-[120px]" />
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div><Label>Trainer</Label>
                <Input value={course.trainer || ""} onChange={(e) => upd("trainer", e.target.value)} className="mt-1.5 rounded-xl h-11" />
              </div>
              <div><Label>Duration (hours)</Label>
                <Input type="number" value={course.duration_hours || 0} onChange={(e) => upd("duration_hours", parseInt(e.target.value) || 0)} className="mt-1.5 rounded-xl h-11" />
              </div>
              <div><Label>Difficulty</Label>
                <select value={course.difficulty || "Beginner"} onChange={(e) => upd("difficulty", e.target.value)}
                  className="mt-1.5 w-full h-11 rounded-xl bg-transparent border border-border px-3 text-sm">
                  {DIFFICULTIES.map((d) => <option key={d}>{d}</option>)}
                </select>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Category</Label>
                <select value={course.category || "Marketing"} onChange={(e) => upd("category", e.target.value)}
                  className="mt-1.5 w-full h-11 rounded-xl bg-transparent border border-border px-3 text-sm">
                  {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="flex items-end gap-6">
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={!!course.featured} onCheckedChange={(v) => upd("featured", v)} /> Featured
                </label>
              </div>
            </div>
            <Button onClick={saveDetails} disabled={saving} data-testid="editor-save-details"
              className="rounded-full royal-gradient border-0 text-white btn-lift">
              <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving..." : "Save changes"}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="curriculum" className="mt-5 space-y-5">
          <div className="rounded-3xl border border-dashed border-border p-4 flex items-center gap-2 max-w-3xl">
            <Input value={newModuleTitle} onChange={(e) => setNewModuleTitle(e.target.value)}
              placeholder="New module title (e.g. Module 1 — Fundamentals)"
              data-testid="new-module-title"
              className="rounded-xl h-11" />
            <Button onClick={addModule} data-testid="add-module-btn"
              className="rounded-full royal-gradient border-0 text-white btn-lift">
              <Plus className="h-4 w-4 mr-1" /> Add module
            </Button>
          </div>

          {(course.modules || []).length === 0 && (
            <div className="rounded-3xl border border-border bg-card p-10 text-center text-sm text-muted-foreground">
              No modules yet. Add your first module above.
            </div>
          )}

          {(course.modules || []).map((m) => (
            <ModuleBlock key={m.id} courseId={course.id} module={m} onChange={load} />
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
